import { MdClose } from "react-icons/md";
import jsPDF from "jspdf";
import { FaDownload } from "react-icons/fa";
import htmlcss from "../../assets/img/htmlcss.jpg";
import uidesign from "../../assets/img/uidesign.jpg";
import python from "../../assets/img/python.jpg";
import uxdesign from "../../assets/img/uxdesign.jpg";

const curriculumImageMap = {
  "digital-literacy": uxdesign,
  "web-design": htmlcss,
  "video-animation": uxdesign,
  "product-design": uidesign,
  "python-programming": python,
  // If there's a specific course ID for "Product Design: UI Design", it would go here.
  // For now, the UI design image is included but not directly mapped to a course ID from the provided `getCurriculumContent`
};

const CurriculumModal = ({ isOpen, onClose, course }) => {
  if (!isOpen || !course) return null;

  const handleDownload = async () => {
    const imageUrl = curriculumImageMap[course.id];
    if (!imageUrl) {
      alert("Curriculum image not available for download.");
      return;
    }

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.onload = () => {
        const imgData = reader.result;
        const img = new Image();
        img.onload = () => {
          const imgWidth = img.width;
          const imgHeight = img.height;

          // Determine orientation based on image aspect ratio
          const orientation = imgWidth > imgHeight ? "l" : "p"; // 'l' for landscape, 'p' for portrait
          const doc = new jsPDF(orientation, "pt", "a4");

          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          const aspectRatio = imgWidth / imgHeight;
          let finalWidth = pageWidth;
          let finalHeight = pageHeight;

          if (imgWidth > imgHeight) {
            // Landscape image
            finalHeight = pageWidth / aspectRatio;
            if (finalHeight > pageHeight) {
              // If height still too big for landscape A4
              finalHeight = pageHeight;
              finalWidth = pageHeight * aspectRatio;
            }
          } else {
            // Portrait image
            finalWidth = pageHeight * aspectRatio;
            if (finalWidth > pageWidth) {
              // If width still too big for portrait A4
              finalWidth = pageWidth;
              finalHeight = pageWidth / aspectRatio;
            }
          }

          // Center the image on the page
          const x = (pageWidth - finalWidth) / 2;
          const y = (pageHeight - finalHeight) / 2;

          doc.addImage(imgData, "JPEG", x, y, finalWidth, finalHeight);
          doc.save(`${course.title}-Curriculum.pdf`);
        };
        img.src = imgData;
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error("Error downloading curriculum as PDF:", error);
      alert("Failed to download curriculum. Please try again.");
    }
  };

  const currentCurriculumImage =
    curriculumImageMap[course.id] || "/placeholder.svg?height=720&width=1280";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-[#113296]">
            {course.title} - Curriculum Preview
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose className="w-6 h-6" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="mb-6">
            <img
              src={currentCurriculumImage || "/placeholder.svg"}
              alt={`${course.title} Curriculum Preview`}
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </div>
          {/* The text content below is kept for display, but the download button now downloads the image as PDF */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-[#113296]">
              Course Overview
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {course.description}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#113296]">Duration</h4>
                <p className="text-gray-700">{course.duration}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#113296]">Age Group</h4>
                <p className="text-gray-700">{course.age}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-[#113296]">Price</h4>
                <p className="text-gray-700">{course.price}</p>
              </div>
            </div>
            {/* Weekly Breakdown - Displaying the image, so this section might be redundant if the image covers it */}
            <div className="mt-6">
              <h4 className="text-lg font-semibold text-[#113296] mb-3">
                Weekly Learning Plan
              </h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                  {/* You can keep a text summary here if the image doesn't fully replace it */}
                  Detailed curriculum content is available in the image above.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            className="px-6 py-2 bg-[#FF4B01] text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <FaDownload className="w-4 h-4" />
            Download Curriculum
          </button>
        </div>
      </div>
    </div>
  );
};

export default CurriculumModal;

// import { Download } from "@mui/icons-material";
// import { MdClose } from "react-icons/md";

// const CurriculumModal = ({ isOpen, onClose, course }) => {
//   if (!isOpen) return null;

//   const handleDownload = () => {
//     // Create curriculum content based on the course
//     const curriculumContent = `
// CEPHAS KIDS TECH EXTRA - ${course.title.toUpperCase()} CURRICULUM

// Course Overview:
// ${course.description}

// Course Details:
// - Duration: ${course.duration}
// - Age Group: ${course.age}
// - Price: ${course.price}
// - Requirements: ${course.requirements}

// Learning Objectives:
// ${getCurriculumContent(course.id)}

// Assessment Methods:
// - Practical projects and assignments
// - Peer collaboration and presentations
// - Portfolio development
// - Final project showcase

// Certification:
// Upon successful completion, students will receive a certificate of completion from Cephas ICT Hub.

// Contact Information:
// Phone: +2348141335945
// Email: cephasict@gmail.com
// Website: www.cephasict.com

// © ${new Date().getFullYear()} Cephas ICT Hub - Kids Tech Extra
//     `;

//     // Create and download the file
//     const blob = new Blob([curriculumContent], {
//       type: "text/plain;charset=utf-8",
//     });
//     const url = window.URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.href = url;
//     link.download = `${course.title}-Curriculum.txt`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//     window.URL.revokeObjectURL(url);
//   };

//   const getCurriculumContent = (courseId) => {
//     const curriculums = {
//       "digital-literacy": `
// Week 1: Computer Basics
// - Understanding hardware and software components
// - Basic computer operations and file management
// - Introduction to operating systems

// Week 2: Internet and Online Safety
// - Internet basics and web browsing
// - Online safety and digital citizenship
// - Email basics and communication

// Week 3: Productivity Tools
// - Microsoft Office basics (Word, Excel, PowerPoint)
// - Google Workspace introduction
// - Document creation and formatting

// Week 4: Digital Skills Application
// - Creating presentations and documents
// - Basic troubleshooting
// - Final project and assessment`,

//       "web-design": `
// Week 1: Introduction to Web Design
// - Understanding the web and how websites work
// - Introduction to HTML basics
// - Creating your first webpage

// Week 2: Styling with CSS
// - CSS fundamentals and selectors
// - Colors, fonts, and basic styling
// - Layout basics and positioning

// Week 3: Responsive Design
// - Making websites mobile-friendly
// - CSS Grid and Flexbox basics
// - Media queries introduction

// Week 4: Project Development
// - Building a complete website
// - Adding interactivity
// - Publishing and sharing your website`,

//       "video-animation": `
// Week 1: Animation Fundamentals
// - Understanding animation principles
// - Introduction to animation software
// - Creating basic movements and transitions

// Week 2: Character Design and Development
// - Designing simple characters
// - Character rigging and setup
// - Basic character animation

// Week 3: Storytelling and Storyboarding
// - Creating compelling stories
// - Storyboard development
// - Scene planning and composition

// Week 4: Final Project
// - Creating a complete animated video
// - Adding sound and effects
// - Presenting and sharing your animation`,

//       "product-design": `
// Week 1: Design Thinking
// - Understanding user needs and problems
// - Introduction to design thinking process
// - Research and ideation techniques

// Week 2: Prototyping and Wireframing
// - Creating wireframes and mockups
// - Basic prototyping tools
// - User interface design principles

// Week 3: Visual Design
// - Color theory and typography
// - Creating visual designs
// - Design systems and consistency

// Week 4: Testing and Iteration
// - User testing basics
// - Gathering feedback
// - Iterating and improving designs`,

//       "python-programming": `
// Week 1: Programming Fundamentals
// - Introduction to Python and programming
// - Variables, data types, and basic operations
// - Writing your first Python programs

// Week 2: Control Structures
// - Conditional statements (if/else)
// - Loops and iterations
// - Functions and code organization

// Week 3: Data Structures and Problem Solving
// - Lists, dictionaries, and data manipulation
// - Problem-solving techniques
// - Debugging and error handling

// Week 4: Project Development
// - Building a complete Python application
// - Working with files and data
// - Final project presentation`,
//     };

//     return (
//       curriculums[courseId] ||
//       "Detailed curriculum content will be provided during enrollment."
//     );
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b">
//           <h2 className="text-2xl font-bold text-[#113296]">
//             {course.title} - Curriculum Preview
//           </h2>
//           <button
//             onClick={onClose}
//             className="p-2 hover:bg-gray-100 rounded-full transition-colors"
//           >
//             <MdClose className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
//           <div className="mb-6">
//             <img
//               src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Cephas%20Kids%20Curriculum.jpg-rOtc9g3zQiskIKzTuE6RnGhxagyeTV.jpeg"
//               alt="Curriculum Preview"
//               className="w-full h-auto rounded-lg shadow-lg"
//             />
//           </div>

//           <div className="space-y-4">
//             <h3 className="text-xl font-semibold text-[#113296]">
//               Course Overview
//             </h3>
//             <p className="text-gray-700 leading-relaxed">
//               {course.description}
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Duration</h4>
//                 <p className="text-gray-700">{course.duration}</p>
//               </div>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Age Group</h4>
//                 <p className="text-gray-700">{course.age}</p>
//               </div>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Price</h4>
//                 <p className="text-gray-700">{course.price}</p>
//               </div>
//             </div>

//             {/* Weekly Breakdown */}
//             <div className="mt-6">
//               <h4 className="text-lg font-semibold text-[#113296] mb-3">
//                 Weekly Learning Plan
//               </h4>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
//                   {getCurriculumContent(course.id)}
//                 </pre>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
//           <button
//             onClick={onClose}
//             className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
//           >
//             Close
//           </button>
//           <button
//             onClick={handleDownload}
//             className="px-6 py-2 bg-[#FF4B01] text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
//           >
//             <Download className="w-4 h-4" />
//             Download Curriculum
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CurriculumModal;

// import { Download } from "@mui/icons-material";
// import { MdClose } from "react-icons/md";

// const CurriculumModal = ({ isOpen, onClose, course }) => {
//   if (!isOpen) return null;

//   const handleDownload = () => {
//     // For now, let's create a simple PDF content or redirect to a working PDF
//     // You can replace this with your actual PDF files once they're uploaded

//     // Option 1: Create a simple text file as PDF (temporary solution)
//     const pdfContent = `
// Course: ${course.title}
// Duration: ${course.duration}
// Age Group: ${course.age}
// Price: ${course.price}
// Requirements: ${course.requirements}

// Description:
// ${course.description}

// This is a temporary curriculum document. Please replace with actual PDF content.
//     `;

//     const blob = new Blob([pdfContent], { type: "text/plain" });
//     const url = window.URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.href = url;
//     link.download = `${course.title}-Curriculum.pdf`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//     window.URL.revokeObjectURL(url);
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b">
//           <h2 className="text-2xl font-bold text-[#113296]">
//             {course.title} - Curriculum Preview
//           </h2>
//           <button
//             onClick={onClose}
//             className="p-2 hover:bg-gray-100 rounded-full transition-colors"
//           >
//             <MdClose className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
//           <div className="mb-6">
//             <img
//               src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Cephas%20Kids%20Curriculum.jpg-rOtc9g3zQiskIKzTuE6RnGhxagyeTV.jpeg"
//               alt="Curriculum Preview"
//               className="w-full h-auto rounded-lg shadow-lg"
//             />
//           </div>

//           <div className="space-y-4">
//             <h3 className="text-xl font-semibold text-[#113296]">
//               Course Overview
//             </h3>
//             <p className="text-gray-700 leading-relaxed">
//               {course.description}
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Duration</h4>
//                 <p className="text-gray-700">{course.duration}</p>
//               </div>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Age Group</h4>
//                 <p className="text-gray-700">{course.age}</p>
//               </div>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Price</h4>
//                 <p className="text-gray-700">{course.price}</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
//           <button
//             onClick={onClose}
//             className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
//           >
//             Close
//           </button>
//           <button
//             onClick={handleDownload}
//             className="px-6 py-2 bg-[#FF4B01] text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
//           >
//             <Download className="w-4 h-4" />
//             Download Curriculum
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CurriculumModal;

// import { Download } from "@mui/icons-material";
// import { MdClose } from "react-icons/md";

// const CurriculumModal = ({ isOpen, onClose, course }) => {
//   if (!isOpen) return null;

//   const handleDownload = () => {
//     // Create a download link
//     const link = document.createElement("a");
//     link.href = course.curriculumFile;
//     link.download = `${course.title}-Curriculum.pdf`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//       <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
//         {/* Header */}
//         <div className="flex justify-between items-center p-6 border-b">
//           <h2 className="text-2xl font-bold text-[#113296]">
//             {course.title} - Curriculum Preview
//           </h2>
//           <button
//             onClick={onClose}
//             className="p-2 hover:bg-gray-100 rounded-full transition-colors"
//           >
//             <MdClose className="w-6 h-6" />
//           </button>
//         </div>

//         {/* Content */}
//         <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
//           <div className="mb-6">
//             <img
//               src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Cephas%20Kids%20Curriculum.jpg-rOtc9g3zQiskIKzTuE6RnGhxagyeTV.jpeg"
//               alt="Curriculum Preview"
//               className="w-full h-auto rounded-lg shadow-lg"
//             />
//           </div>

//           <div className="space-y-4">
//             <h3 className="text-xl font-semibold text-[#113296]">
//               Course Overview
//             </h3>
//             <p className="text-gray-700 leading-relaxed">
//               {course.description}
//             </p>

//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Duration</h4>
//                 <p className="text-gray-700">{course.duration}</p>
//               </div>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Age Group</h4>
//                 <p className="text-gray-700">{course.age}</p>
//               </div>
//               <div className="bg-gray-50 p-4 rounded-lg">
//                 <h4 className="font-semibold text-[#113296]">Price</h4>
//                 <p className="text-gray-700">{course.price}</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
//           <button
//             onClick={onClose}
//             className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
//           >
//             Close
//           </button>
//           <button
//             onClick={handleDownload}
//             className="px-6 py-2 bg-[#FF4B01] text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
//           >
//             <Download className="w-4 h-4" />
//             Download Curriculum
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default CurriculumModal;
